import * as React from 'react';
import { FalconUIRenderingContext, SourceLanguage, Link } from '@nutmeglabs/falcon-ui';
import { ReduxState } from 'ducks';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { useQueryString } from 'hooks/useQueryString';
import { appendQueryString } from 'lib/util/appendQueryString';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { selectIsPMP } from 'lib/util/privateMarketplace';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  children: React.ReactNode;
}

export const FalconUIContextProvider = ({ children }: Props) => {
  const { t, i18n } = useTranslation();
  const currencyFormatter = useCurrencyFormatter();

  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedLanguage = Object.entries(languageOptionMap).find(([key, value]) => {
    return value.iso === i18n.language;
  })?.[0] as SourceLanguage;

  const languageOptions = useSelector((state: ReduxState) => {
    return state.server.settings.all.supported_languages?.map((lang) => ({
      value: lang,
      text: languageOptionMap[lang].name,
    }));
  });
  const isPMP = useSelector(selectIsPMP);

  const handleLanguageChange = React.useCallback(
    (lang: SourceLanguage) => {
      if (isPMP) {
        const pmpLink = settings.private_marketplace_links?.find((link) => link.language === lang);
        if (pmpLink) {
          window.location.href = pmpLink.url;
        } else {
          const urlParts = window.location.href.split('?');
          const searchParams = new URLSearchParams(urlParts.length > 1 ? urlParts[1] : '');
          searchParams.set('lng', languageOptionMap[lang].iso);

          window.location.href = `https://${
            settings.booking_widget_domain
          }?${searchParams.toString()}`;
        }
      } else {
        const urlParts = window.location.href.split('?');
        const searchParams = new URLSearchParams(urlParts.length > 1 ? urlParts[1] : '');
        searchParams.set('lng', languageOptionMap[lang].iso);

        window.location.href = `${urlParts[0]}?${searchParams.toString()}`;
      }
    },
    [isPMP, settings]
  );

  const queryString = useQueryString();

  return (
    <FalconUIRenderingContext.Provider
      value={{
        companyName: settings.supplier_name || '',
        companyLogoUrl: settings.logo_url || '',
        language: selectedLanguage,
        setLanguage: handleLanguageChange,
        supportedLanguages: languageOptions,
        t: (key: string, args?: Record<string, string>) => {
          switch (key) {
            case 'Sun':
              return t('Sun');
            case 'Mon':
              return t('Mon');
            case 'Tue':
              return t('Tue');
            case 'Wed':
              return t('Wed');
            case 'Thu':
              return t('Thu');
            case 'Fri':
              return t('Fri');
            case 'Sat':
              return t('Sat');
            case 'Days of Week: {{daysOfWeek}}':
              return t('Days of Week: {{daysOfWeek}}', args);
            case 'Hours: {{operatingHours}}':
              return t('Hours: {{operatingHours}}', args);
          }

          return t(key);
        },
        formatCurrencyAmount: currencyFormatter.format,

        linkFormatter: (link?: Link): string => {
          if (!link) {
            return '';
          }
          switch (link.type) {
            case 'PRODUCT':
              return isPMP
                ? appendQueryString(`/top/products/${link.productId}`, queryString)
                : appendQueryString(`/products/${link.productId}`, queryString);
            case 'CUSTOM_PAGE':
              return appendQueryString(link.customPagePath || '', queryString);
            case 'FULL_URL':
              return link.fullUrl || '';
            case 'PRIVATE_MARKETPLACE':
              return appendQueryString(link.privateMarketplacePath || '', queryString);
          }
          return '';
        },
      }}
    >
      {children}
    </FalconUIRenderingContext.Provider>
  );
};
